import { useEffect } from "react";

const SITE_URL = "https://asadhatimi.com";
const DEFAULT_IMAGE = `${SITE_URL}/asad_hero.png`;

function setMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
}

function buildSchema(page, pathname) {
  const person = {
    "@type": "Person",
    "@id": `${SITE_URL}/#person`,
    name: "Asad Hatimi",
    url: SITE_URL,
    image: `${SITE_URL}/asad_hero_portrait.jpg`,
    jobTitle: "Industrial Project Management and CAPEX Governance Consultant",
    sameAs: ["https://www.linkedin.com/in/asad-hatimi-program-leader"],
    knowsAbout: [
      "Industrial project management",
      "Capital project governance",
      "Project controls",
      "Primavera P6",
      "Cement projects",
      "Steel projects",
      "Project recovery",
      "Independent directorship"
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Udaipur",
      addressRegion: "Rajasthan",
      addressCountry: "IN"
    }
  };

  if (!page) {
    return {
      "@context": "https://schema.org",
      "@graph": [
        person,
        {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          url: `${SITE_URL}/`,
          name: "Asad Hatimi",
          description: "Industrial project management, CAPEX governance and independent board advisory.",
          publisher: { "@id": `${SITE_URL}/#person` },
          inLanguage: "en-IN"
        },
        {
          "@type": "ProfilePage",
          "@id": `${SITE_URL}/#profile`,
          url: `${SITE_URL}/`,
          name: "Asad Hatimi — Industrial Project Leadership",
          mainEntity: { "@id": `${SITE_URL}/#person` },
          isPartOf: { "@id": `${SITE_URL}/#website` },
          inLanguage: "en-IN"
        }
      ]
    };
  }

  const url = `${SITE_URL}${pathname}/`;
  const basePage = {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: page.title,
    description: page.description,
    about: { "@id": `${SITE_URL}/#person` },
    author: { "@id": `${SITE_URL}/#person` },
    inLanguage: "en-IN"
  };

  if (page.type === "article") {
    return {
      "@context": "https://schema.org",
      "@graph": [
        person,
        {
          ...basePage,
          "@type": "Article",
          headline: page.h1,
          datePublished: page.published,
          dateModified: page.published,
          image: DEFAULT_IMAGE,
          mainEntityOfPage: { "@id": `${url}#webpage` }
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Insights", item: `${SITE_URL}/insights/` },
            { "@type": "ListItem", position: 3, name: page.h1, item: url }
          ]
        }
      ]
    };
  }

  return {
    "@context": "https://schema.org",
    "@graph": [
      person,
      basePage,
      {
        "@type": "Service",
        "@id": `${url}#service`,
        name: page.h1,
        description: page.description,
        provider: { "@id": `${SITE_URL}/#person` },
        areaServed: { "@type": "Country", name: "India" },
        url
      },
      {
        "@type": "FAQPage",
        mainEntity: (page.faqs || []).map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer }
        }))
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: page.h1, item: url }
        ]
      }
    ]
  };
}

export default function SeoHead({ page, pathname = "/", noindex = false }) {
  useEffect(() => {
    const isHome = pathname === "/";
    const title = page?.title || "Asad Hatimi | Industrial Project Management & CAPEX Governance";
    const description =
      page?.description ||
      "Asad Hatimi provides industrial project management, CAPEX governance, project recovery and independent board advisory across cement, steel, power and EPC.";
    const canonical = isHome ? `${SITE_URL}/` : `${SITE_URL}${pathname}/`;

    document.title = title;
    document.documentElement.lang = "en-IN";

    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"
    });
    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:type"]', {
      property: "og:type",
      content: page?.type === "article" ? "article" : "website"
    });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setMeta('meta[property="og:image"]', { property: "og:image", content: DEFAULT_IMAGE });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: DEFAULT_IMAGE });

    let canonicalElement = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalElement) {
      canonicalElement = document.createElement("link");
      canonicalElement.rel = "canonical";
      document.head.appendChild(canonicalElement);
    }
    canonicalElement.href = canonical;

    let schemaElement = document.getElementById("seo-jsonld");
    if (!schemaElement) {
      schemaElement = document.createElement("script");
      schemaElement.id = "seo-jsonld";
      schemaElement.type = "application/ld+json";
      document.head.appendChild(schemaElement);
    }
    schemaElement.textContent = JSON.stringify(buildSchema(page, pathname));
  }, [page, pathname, noindex]);

  return null;
}
